import { memo } from 'react'

import { Box, StatusLabel, Text } from '@factorialco/deprecated-design-system'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import AssigneesRow from './AssigneesRow'
import TimelineItemWrapper, {
  FutureIcon,
  getContentIcon,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getStatusType,
} from './TimelineItemWrapper'
import type { ApprovalsV2ApprovalTimelineEntry, ItemStatus, TimelineItemEmployeeFragment } from './types'
import { STATUS_COLORS } from './types'

interface Props {
  entry: ApprovalsV2ApprovalTimelineEntry
  hasConnectingLine?: boolean
  alert?: React.ReactNode
  daysOverdue?: number
  employees?: TimelineItemEmployeeFragment[]
}

const formatTimestamp = (timestamp: string, isFuture: boolean): string => {
  const date = stringToDate(timestamp, 'backend_datetime')
  const formattedDate = i18nDateToString(date, isFuture ? 'ui' : 'ui_short_with_time')

  return isFuture
    ? i18n.t('approvals_v2.timeline.estimated_on', { date: formattedDate })
    : i18n.t('approvals_v2.timeline.requested_on', { date: formattedDate })
}

const TimelineItem = memo(
  ({ entry, hasConnectingLine = false, alert, daysOverdue, employees = [] }: Props) => {
    const { title, status, time: timestamp, type, actionGroup } = entry
    const isCannotResolve = type === 'cannot_resolve'
    const isEntryOverdue = status === 'running' && daysOverdue && daysOverdue > 0
    const displayStatus: ItemStatus = isEntryOverdue ? 'overdue' : status
    const statusLabel = getStatusLabel(displayStatus)
    const statusType = getStatusType(displayStatus)
    const statusIcon = isCannotResolve ? <FutureIcon /> : getStatusIcon(displayStatus)
    const statusColor = isCannotResolve ? STATUS_COLORS.default : getStatusColor(displayStatus)
    const contentIcon = getContentIcon(type, actionGroup)

    if (isCannotResolve) {
      return (
        <TimelineItemWrapper
          statusIcon={statusIcon}
          contentIcon={contentIcon}
          hasConnectingLine={hasConnectingLine}
          statusColor={statusColor}
        >
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {i18n.t('approvals_v2.timeline.cannot_resolve.title')}
          </Text.Normal>
          <Text.Normal size="200" color="grey600">
            {i18n.t('approvals_v2.timeline.cannot_resolve.description')}
          </Text.Normal>
        </TimelineItemWrapper>
      )
    }

    return (
      <TimelineItemWrapper
        statusIcon={statusIcon}
        contentIcon={contentIcon}
        hasConnectingLine={hasConnectingLine}
        statusColor={statusColor}
        alert={alert}
      >
        <Box gap="s12">
          <Box gap="s4">
            <Box flexDirection="row" alignItems="center" gap="s8" justifyContent="spaceBetween">
              <Text.Normal weight="medium" color="grey1000">
                {title}
              </Text.Normal>
              <StatusLabel type={statusType}>{statusLabel}</StatusLabel>
            </Box>
            <Text.Normal size="200" color="grey700">
              {formatTimestamp(timestamp, status === 'future')}
            </Text.Normal>
          </Box>
          <AssigneesRow
            assignees={entry.assignees ?? []}
            employees={employees}
            totalAssigneesCount={entry.totalAssigneesCount}
          />
        </Box>
      </TimelineItemWrapper>
    )
  }
)

TimelineItem.displayName = 'TimelineItem'

export default TimelineItem
